import { useEffect, useRef, useState } from 'react'

export function CursorSpotlight() {
  const containerRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const targetPosition = useRef({ x: 0, y: 0 })
  const currentPosition = useRef({ x: 0, y: 0 })
  const isVisible = useRef(false)
  const isReducedMotion = useRef(false)
  const isPointerFine = useRef(false)
  const [isEnabled, setIsEnabled] = useState(true)
  const [canRun, setCanRun] = useState(false)
  const isEnabledRef = useRef(true)

  // Lerp function for smooth interpolation
  const lerp = (start: number, end: number, factor: number) => {
    return start + (end - start) * factor
  }

  // Animation loop using requestAnimationFrame
  const animate = () => {
    if (!isVisible.current || !containerRef.current || !isEnabledRef.current) {
      rafRef.current = requestAnimationFrame(animate)
      return
    }

    // Smooth interpolation
    currentPosition.current.x = lerp(currentPosition.current.x, targetPosition.current.x, 0.3)
    currentPosition.current.y = lerp(currentPosition.current.y, targetPosition.current.y, 0.3)

    // Update CSS custom properties
    if (containerRef.current) {
      containerRef.current.style.setProperty('--sx', `${currentPosition.current.x}px`)
      containerRef.current.style.setProperty('--sy', `${currentPosition.current.y}px`)
    }

    rafRef.current = requestAnimationFrame(animate)
  }

  // Mouse move handler
  const handleMouseMove = (e: MouseEvent) => {
    targetPosition.current.x = e.clientX
    targetPosition.current.y = e.clientY
    isVisible.current = true
  }

  // Mouse leave handler
  const handleMouseLeave = () => {
    isVisible.current = false
    if (containerRef.current) {
      containerRef.current.style.opacity = '0'
    }
  }

  // Mouse enter handler
  const handleMouseEnter = () => {
    isVisible.current = true
    if (containerRef.current) {
      containerRef.current.style.opacity = '1'
    }
  }

  useEffect(() => {
    // Check for reduced motion preference
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    isReducedMotion.current = reducedMotionQuery.matches

    const handleReducedMotionChange = (e: MediaQueryListEvent) => {
      isReducedMotion.current = e.matches
      setCanRun(!e.matches && isPointerFine.current)
    }

    // Check for pointer type
    const pointerQuery = window.matchMedia('(pointer: fine)')
    isPointerFine.current = pointerQuery.matches

    const handlePointerChange = (e: MediaQueryListEvent) => {
      isPointerFine.current = e.matches
      setCanRun(!isReducedMotion.current && e.matches)
    }

    // Handle spotlight toggle
    const handleSpotlightToggle = (e: CustomEvent) => {
      setIsEnabled(e.detail.enabled)
      isEnabledRef.current = e.detail.enabled
    }

    // Initialize spotlight state from localStorage
    const savedSpotlight = localStorage.getItem('spotlight-enabled') !== 'false'
    setIsEnabled(savedSpotlight)
    isEnabledRef.current = savedSpotlight

    // Check if we can run the spotlight
    const canRunSpotlight = !isReducedMotion.current && isPointerFine.current
    setCanRun(canRunSpotlight)

    // Don't initialize if reduced motion or not fine pointer
    if (!canRunSpotlight) {
      return
    }

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove, { passive: true })
    document.addEventListener('mouseleave', handleMouseLeave)
    document.addEventListener('mouseenter', handleMouseEnter)
    reducedMotionQuery.addEventListener('change', handleReducedMotionChange)
    pointerQuery.addEventListener('change', handlePointerChange)
    window.addEventListener('spotlight-toggle', handleSpotlightToggle as EventListener)

    // Start animation loop
    rafRef.current = requestAnimationFrame(animate)

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseleave', handleMouseLeave)
      document.removeEventListener('mouseenter', handleMouseEnter)
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange)
      pointerQuery.removeEventListener('change', handlePointerChange)
      window.removeEventListener('spotlight-toggle', handleSpotlightToggle as EventListener)
      
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  // Don't render if user prefers reduced motion or doesn't have fine pointer
  if (!canRun) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className="fixed top-0 left-0 w-screen h-screen pointer-events-none z-50 transition-opacity duration-300 ease-out"
      style={{
        '--sx': '0px',
        '--sy': '0px',
        willChange: 'transform',
        transform: 'translate3d(0, 0, 0)',
        opacity: isEnabled ? '1' : '0',
      } as React.CSSProperties & { '--sx': string; '--sy': string }}
    >
      {/* Main spotlight layer */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full"
        style={{
          left: 'var(--sx)',
          top: 'var(--sy)',
          marginLeft: '-300px',
          marginTop: '-300px',
          background: 'radial-gradient(circle, rgba(27, 59, 122, 0.15) 0%, rgba(167, 194, 255, 0.12) 30%, rgba(27, 59, 122, 0.08) 60%, transparent 100%)',
          filter: 'blur(50px)',
          willChange: 'transform',
          transform: 'translate3d(0, 0, 0)',
        }}
      />
      
      {/* Inner core layer */}
      <div
        className="absolute w-[300px] h-[300px] rounded-full"
        style={{
          left: 'var(--sx)',
          top: 'var(--sy)',
          marginLeft: '-150px',
          marginTop: '-150px',
          background: 'radial-gradient(circle, rgba(167, 194, 255, 0.25) 0%, rgba(27, 59, 122, 0.15) 50%, transparent 100%)',
          filter: 'blur(25px)',
          willChange: 'transform',
          transform: 'translate3d(0, 0, 0)',
        }}
      />
    </div>
  )
}